import { apiFetch, readApiJson } from '@/lib/api-fetch'
import { parseProductAnalyticsPublicDto } from '@/lib/product-analytics-public'

export type ManufacturerSalesShares = ReadonlyMap<string, number>

type ManufacturerBreakdownRow = { key: string; label: string; revenueKopecks: number }

type ManufacturerBreakdown = {
  availability: 'available' | 'unavailable'
  totalRevenueKopecks: number
  manufacturers: readonly ManufacturerBreakdownRow[]
}

export function manufacturerSalesSharesFromBreakdown(breakdown: ManufacturerBreakdown): ManufacturerSalesShares {
  if (breakdown.availability !== 'available' || breakdown.totalRevenueKopecks <= 0) return new Map()

  return new Map(
    breakdown.manufacturers
      .filter((manufacturer) => manufacturer.revenueKopecks > 0)
      .map((manufacturer) => [
        manufacturer.key,
        manufacturer.revenueKopecks / breakdown.totalRevenueKopecks,
      ]),
  )
}

export function trailingFiveMonthRange(now = new Date()): { from: string; to: string } {
  const start = new Date(now.getFullYear(), now.getMonth() - 4, 1)
  const calendarDate = (value: Date) => {
    const year = value.getFullYear()
    const month = String(value.getMonth() + 1).padStart(2, '0')
    const day = String(value.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }
  return { from: calendarDate(start), to: calendarDate(now) }
}

export function formatManufacturerSalesShare(share: number): string {
  return new Intl.NumberFormat('ru-RU', {
    style: 'percent',
    maximumFractionDigits: 1,
    minimumFractionDigits: share > 0 && share < 0.01 ? 1 : 0,
  }).format(share)
}

export async function loadManufacturerSalesShares(signal?: AbortSignal): Promise<ManufacturerSalesShares> {
  const range = trailingFiveMonthRange()
  const parameters = new URLSearchParams(range)
  const response = await apiFetch(`/api/analytics/products?${parameters.toString()}`, { signal })
  const payload = parseProductAnalyticsPublicDto(await readApiJson(response))
  const breakdown = payload.period?.breakdown.current
  if (breakdown === undefined) return new Map()
  return manufacturerSalesSharesFromBreakdown(breakdown)
}
